import type { Sessao } from './auth';
import { CABECALHO_CSRF } from './auth';
import type { Falha, Resultado } from './acesso';
import { buscar } from './buscar';
import { pedirLancados } from './lancados-no-periodo';
import type { LancadosNoPeriodo } from './lancados-no-periodo';

/** BLOCO 6 — o front de /api/paradao (tela 27). */

export type EstadoDeVenda =
  | { readonly estado: 'sem_venda_ha'; readonly dias: number; readonly ultimaVenda: string }
  | { readonly estado: 'nunca_vendido' }
  | { readonly estado: 'indisponivel' };

export type ItemDoParadao = {
  readonly productId: string;
  readonly descricao: string;
  readonly codigo: string | null;
  readonly fabricante: string | null;
  readonly estoque: string | null;
  readonly custoParado: string | null;
  readonly curva: 'A' | 'B' | 'C' | null;
  readonly vendas30: string | null;
  readonly vendas90: string | null;
  readonly venda: EstadoDeVenda;
  readonly tambemPreVencido: boolean;
  readonly origem: 'manual' | 'planilha' | 'migracao_1_0';
  readonly ativo: boolean;
  readonly versao: number;
  /** Unidades lançadas no paradão no período do filtro (só quando a tela manda o período). */
  readonly lancadosNoPeriodo?: number;
};

export type FonteDeVendas = {
  readonly temBase: boolean;
  readonly primeiraVenda: string | null;
  readonly ultimaVenda: string | null;
  readonly importadoEm: string | null;
};

export type CatalogoDoParadao = {
  readonly itens: readonly ItemDoParadao[];
  readonly total: number;
  readonly resumo: {
    readonly comEstoque: number;
    readonly semEstoque: number;
    readonly mais180ComEstoque: number;
    readonly nuncaVendidos: number;
    readonly custoParado: string | null;
  };
  readonly fonte: FonteDeVendas;
  readonly pagina: number;
  readonly tamanhoDaPagina: number;
};

export type DetalheDoParadao = ItemDoParadao & {
  readonly ean: string | null;
  readonly grupo: string | null;
  readonly venderPor: string | null;
  readonly pmc: string | null;
  readonly premioBase: string;
  readonly lotePreVencidoMaisProximo: string | null;
  readonly vendasPorMes: readonly { readonly mes: string; readonly unidades: string }[];
  readonly eventos: readonly {
    readonly tipo: string;
    readonly motivo: string | null;
    readonly ator: string | null;
    readonly origem: string;
    readonly criadoEm: string;
  }[];
  readonly fonte: FonteDeVendas;
};

async function ler<T>(caminho: string): Promise<Resultado<T>> {
  let r: Response;
  try {
    r = await buscar(caminho, { credentials: 'same-origin' });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.ok) return { ok: true, dados: (await r.json()) as T };
  if (r.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  if (r.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  if (r.status === 404)
    return {
      ok: false,
      falha: { tipo: 'conflito', mensagem: 'Este produto não está no Paradão.' },
    };
  return { ok: false, falha: { tipo: 'inesperado', codigo: r.headers.get('x-request-id') } };
}

async function escrever(
  sessao: Sessao,
  caminho: string,
  corpo: unknown,
): Promise<{ ok: true } | { ok: false; falha: Falha }> {
  let r: Response;
  try {
    r = await fetch(caminho, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [CABECALHO_CSRF]: sessao.csrfToken },
      body: JSON.stringify(corpo),
    });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.ok) return { ok: true };
  let mensagem = '';
  try {
    mensagem = ((await r.json()) as { error?: { message?: string } }).error?.message ?? '';
  } catch {
    // sem corpo
  }
  if (r.status === 409 || r.status === 404)
    return { ok: false, falha: { tipo: 'conflito', mensagem } };
  if (r.status === 400) return { ok: false, falha: { tipo: 'invalido', mensagem } };
  if (r.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  if (r.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  return { ok: false, falha: { tipo: 'inesperado', codigo: r.headers.get('x-request-id') } };
}

export function lerParadao(q: {
  filtro: string;
  busca: string;
  pagina: number;
  lancados?: LancadosNoPeriodo | null | undefined;
}) {
  const p = new URLSearchParams({ filtro: q.filtro, pagina: String(q.pagina) });
  if (q.busca.trim()) p.set('busca', q.busca.trim());
  pedirLancados(p, q.lancados);
  return ler<CatalogoDoParadao>(`/api/paradao?${p.toString()}`);
}

export const lerDetalheDoParadao = (id: string) => ler<DetalheDoParadao>(`/api/paradao/${id}`);

export const incluirNoParadao = (sessao: Sessao, id: string, motivo: string | null) =>
  escrever(sessao, `/api/paradao/${id}`, { motivo });

export const tirarDoParadao = (
  sessao: Sessao,
  id: string,
  versaoEsperada: number,
  motivo: string,
) => escrever(sessao, `/api/paradao/${id}/saida`, { versaoEsperada, motivo });

/** O estado de venda em texto — cada um com o seu, nunca um número mágico. */
export function textoDaVenda(v: EstadoDeVenda): {
  data: string;
  dias: string;
  rotulo: string | null;
} {
  if (v.estado === 'indisponivel')
    return { data: 'indisponível', dias: '—', rotulo: 'Última venda indisponível' };
  if (v.estado === 'nunca_vendido')
    return { data: 'nunca vendido', dias: '—', rotulo: 'Nunca vendido' };
  const [a, m, d] = v.ultimaVenda.split('-');
  return { data: `${d ?? ''}/${m ?? ''}/${a ?? ''}`, dias: String(v.dias), rotulo: null };
}
